<template>
  <div class="p-4 surface-card shadow-2 border-round">
    <div class="mb-4 flex justify-content-between align-items-center flex-wrap gap-2">
      <h2 class="text-2xl font-bold">Gestione Appuntamenti Responsabili</h2>
    </div>

    <TabView v-model:activeIndex="activeTab">
      <TabPanel header="Gestione Slot">
        <div class="mb-4 flex justify-content-start align-items-center flex-wrap gap-2">
          <Dropdown
            v-model="responsabileSelezionato"
            :options="responsabili"
            optionLabel="nome_completo"
            placeholder="Seleziona responsabile"
            class="w-20rem"
            @change="onResponsabileChange"
          />
        </div>

        <div v-if="!responsabileSelezionato" class="flex justify-content-center align-items-center" style="min-height:200px">
          <p class="text-600 text-xl">Seleziona un responsabile per gestire le disponibilità.</p>
        </div>

    <div v-else class="grid p-fluid">
      <!-- Calendario -->
      <div class="col-12 lg:col-4">
        <div class="p-card p-4 h-full flex flex-column align-items-center">
          <h3 class="text-xl font-semibold mb-3">Seleziona un Giorno</h3>
          <Calendar
            v-model="giornoSelezionato"
            @update:modelValue="aggiornaDisponibilita"
            view="date"
            dateFormat="dd/mm/yy"
            inline
            locale="it"
            :showOtherMonths="true"
            :minDate="new Date(new Date().setMonth(new Date().getMonth() - 1))"
            :maxDate="new Date(new Date().setMonth(new Date().getMonth() + 6))"
          />
        </div>
      </div>

      <!-- Lista disponibilità del giorno -->
      <div class="col-12 lg:col-8">
        <div class="p-card p-4 h-full">
          <h3 class="text-xl font-semibold mb-3">
            Disponibilità di <b>{{ responsabileSelezionato?.nome_completo }}</b> per {{ formattaData(giornoSelezionato) }}
          </h3>
          <div v-if="loading" class="flex justify-content-center p-4">
            <ProgressSpinner />
          </div>
          <div v-else>
            <DataTable :value="disponibilitaDelGiorno" class="mt-2" showGridlines>
              <Column header="Orario">
                <template #body="{ data }">
                  {{ data.ora_inizio?.slice(0,5) }} – {{ data.ora_fine?.slice(0,5) }}
                </template>
              </Column>
              <Column header="Posti">
                <template #body="{ data }">
                  <Tag :value="`${data.prenotazioni?.length ?? 0}/${data.max_prenotazioni}`"
                    :severity="data.posti_liberi > 0 ? 'success' : 'danger'" />
                </template>
              </Column>
              <Column header="Prenotazioni">
                <template #body="{ data }">
                  <div v-if="data.prenotazioni && data.prenotazioni.length > 0">
                    <div v-for="p in data.prenotazioni" :key="p.id_prenotazione" class="flex align-items-center gap-2 mb-1">
                      <span>{{ p.nome_atleta || '(nessun atleta)' }}</span>
                      <span v-if="p.nota" class="text-500 text-sm">({{ p.nota }})</span>
                      <Button icon="pi pi-times" class="p-button-rounded p-button-danger p-button-sm"
                        @click="cancellaPrenotazione(p.id_prenotazione)" v-tooltip.top="'Cancella prenotazione'" />
                    </div>
                  </div>
                  <Tag v-else value="Disponibile" severity="success" />
                </template>
              </Column>
              <Column header="Azioni">
                <template #body="{ data }">
                  <div class="flex gap-2">
                    <Button
                      v-if="data.posti_liberi > 0"
                      label="Prenota"
                      icon="pi pi-user-plus"
                      class="p-button-success p-button-sm"
                      @click="apriDialogPrenota(data)"
                    />
                    <Button
                      label="Elimina"
                      icon="pi pi-trash"
                      class="p-button-danger p-button-sm"
                      @click="eliminaDisponibilita(data)"
                      :disabled="data.prenotazioni && data.prenotazioni.length > 0"
                    />
                  </div>
                </template>
              </Column>
              <Column v-if="disponibilitaDelGiorno.length === 0">
                <template #body>
                  <span class="text-600">Nessuna disponibilità per questo giorno.</span>
                </template>
              </Column>
            </DataTable>
            <p v-if="disponibilitaDelGiorno.length === 0" class="text-600 mt-2 text-center">
              Nessuna disponibilità presente per questo giorno.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Sezione aggiunta disponibilità -->
    <div v-if="responsabileSelezionato" class="mt-4 p-card p-4">
      <h3 class="text-xl font-semibold mb-3">
        Aggiungi Disponibilità per {{ responsabileSelezionato?.nome_completo }} – {{ formattaData(giornoSelezionato) }}
      </h3>
      <div class="grid p-fluid">
        <div class="col-12 md:col-2">
          <label>Ora Inizio</label>
          <Calendar
            v-model="nuovaOraInizio"
            timeOnly showTime hourFormat="24"
            :stepMinute="30"
            :minDate="minTime" :maxDate="maxTime"
          />
        </div>
        <div class="col-12 md:col-2">
          <label>Ora Fine</label>
          <Calendar
            v-model="nuovaOraFine"
            timeOnly showTime hourFormat="24"
            :stepMinute="30"
            :minDate="minTime" :maxDate="maxTime"
          />
        </div>
        <div class="col-12 md:col-2">
          <label>Max prenotazioni</label>
          <InputNumber v-model="nuovoMaxPrenotazioni" :min="1" :max="10" />
        </div>
        <div class="col-12 md:col-2">
          <label>Note (opzionale)</label>
          <InputText v-model="nuoveNote" placeholder="Es. colloquio" />
        </div>
        <div class="col-12 md:col-2">
          <label>Ripeti fino al</label>
          <Calendar v-model="dataFineGenerazione" dateFormat="dd/mm/yy" />
        </div>
        <div class="col-12 md:col-2 flex align-items-end">
          <Button
            :label="`Genera slot`"
            icon="pi pi-plus"
            @click="generaDisponibilita"
            :disabled="loading || !nuovaOraInizio || !nuovaOraFine"
            :loading="loading"
          />
        </div>
      </div>
    </div>

    <Toast />

    <!-- Dialog prenotazione atleta -->
    <Dialog v-model:visible="dialogPrenota" header="Prenota Appuntamento" :modal="true" :closable="false" style="width: 30rem;">
      <div class="p-fluid">
        <div class="field">
          <label>Seleziona Atleta</label>
          <AutoComplete
            v-model="atletaSelezionato"
            :suggestions="atletiFiltrati"
            field="nomecompletoUtente"
            placeholder="Cerca atleta"
            dropdown forceSelection :minLength="0"
            @complete="filterAtleti"
          />
        </div>
        <div class="field mt-3">
          <label>Nota (opzionale)</label>
          <InputText v-model="notaPrenotazione" placeholder="Inserisci una nota" />
        </div>
      </div>
      <template #footer>
        <Button label="Annulla" icon="pi pi-times" class="p-button-text" @click="dialogPrenota = false" />
        <Button label="Conferma" icon="pi pi-check" class="p-button-primary"
          @click="confermaPrenotazione"
          :disabled="!atletaSelezionato && notaPrenotazione.trim() === ''" />
      </template>
    </Dialog>
      </TabPanel>

      <TabPanel header="Prospetto Prossime 2 Settimane">
        <div v-if="prospettoLoading" class="flex justify-content-center p-4">
          <ProgressSpinner />
        </div>
        <div v-else>
          <div class="overflow-auto">
            <table class="w-full text-left border-collapse" style="min-width: 600px;">
              <thead>
                <tr>
                  <th class="p-3 border-1 border-300 bg-gray-100">Responsabile</th>
                  <th v-for="giorno in prospettoGiorni" :key="giorno" class="p-3 border-1 border-300 bg-gray-100 text-center">
                    {{ formattaDataCorto(giorno) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="resp in prospettoResponsabili" :key="resp.id">
                  <td class="p-3 border-1 border-300 font-bold">{{ resp.nome_completo }}</td>
                  <td v-for="giorno in prospettoGiorni" :key="giorno" class="p-3 border-1 border-300 text-center">
                    <div v-if="prospettoMatrice[resp.id] && prospettoMatrice[resp.id][giorno]" 
                         class="cursor-pointer hover:bg-blue-50 p-2 border-round transition-colors"
                         @click="apriDettaglio(resp, giorno)"
                         v-tooltip="'Clicca per vedere i dettagli'">
                       <span class="text-green-600 font-bold">{{ prospettoMatrice[resp.id][giorno].occupati }}</span> / 
                       <span class="text-gray-600">{{ prospettoMatrice[resp.id][giorno].totali }}</span>
                    </div>
                    <div v-else class="text-400">-</div>
                  </td>
                </tr>
                <tr v-if="prospettoResponsabili.length === 0">
                  <td :colspan="prospettoGiorni.length + 1" class="p-3 text-center text-600">Nessuno slot trovato nelle prossime 2 settimane.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </TabPanel>
    </TabView>
  </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance } from 'vue';
import { useToast } from 'primevue/usetoast';
import Service from '@/services/levante-api/levante-api';

import Calendar from 'primevue/calendar';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Toast from 'primevue/toast';
import ProgressSpinner from 'primevue/progressspinner';
import Dropdown from 'primevue/dropdown';
import Dialog from 'primevue/dialog';
import AutoComplete from 'primevue/autocomplete';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';

const self = getCurrentInstance();
self.$eventBus = self.appContext.config.globalProperties.$eventBus;
const service = new Service(self.$eventBus);
const toast = useToast();

const responsabili = ref([]);
const responsabileSelezionato = ref(null);
const giornoSelezionato = ref(new Date());
const disponibilitaDelGiorno = ref([]);
const loading = ref(false);

const nuovaOraInizio = ref(null);
const nuovaOraFine = ref(null);
const nuovoMaxPrenotazioni = ref(1);
const nuoveNote = ref('');
const dataFineGenerazione = ref(new Date());

const oggi = new Date();
const minTime = new Date(oggi.getFullYear(), oggi.getMonth(), oggi.getDate(), 7, 0);
const maxTime = new Date(oggi.getFullYear(), oggi.getMonth(), oggi.getDate(), 22, 0);

// Dialog prenota
const dialogPrenota = ref(false);
const disponibilitaSelezionata = ref(null);
const atletaSelezionato = ref(null);
const atletiFiltrati = ref([]);
const notaPrenotazione = ref('');
const listaAtleti = ref([]);

const activeTab = ref(0);
const prospettoGiorni = ref([]);
const prospettoResponsabili = ref([]);
const prospettoMatrice = ref({});
const prospettoLoading = ref(false);

function formattaDataCorto(giornoStr) {
  if (!giornoStr) return '';
  const d = new Date(giornoStr);
  return d.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
}

function formattaData(data) {
  if (!data) return '—';
  return data.toLocaleDateString('it-IT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

function formatDateLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatTimeLocal(date) {
  return date.toTimeString().slice(0, 5);
}

function filterAtleti(event) {
  const q = (event.query || '').toLowerCase();
  atletiFiltrati.value = listaAtleti.value.filter(a =>
    `${a.nome} ${a.cognome}`.toLowerCase().includes(q)
  );
}

async function caricaResponsabili() {
  try {
    const res = await service.getResponsabiliCorsi();
    responsabili.value = res.items || [];
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Errore', detail: 'Impossibile caricare i responsabili', life: 3000 });
  }
}

async function caricaAtleti() {
  try {
    await service.getAtleti().then(res => {
      listaAtleti.value = res.items || [];
      atletiFiltrati.value = res.items || [];
    });
  } catch (e) { /* silenzioso */ }
}

async function aggiornaDisponibilita() {
  if (!responsabileSelezionato.value || !giornoSelezionato.value) return;
  loading.value = true;
  try {
    const giorno = formatDateLocal(giornoSelezionato.value);
    const res = await service.getAppuntamentiDisponibilitaGiorno(responsabileSelezionato.value.id, giorno);
    disponibilitaDelGiorno.value = res.items || [];
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Errore', detail: 'Impossibile caricare le disponibilità', life: 3000 });
    disponibilitaDelGiorno.value = [];
  } finally {
    loading.value = false;
  }
}

function onResponsabileChange() {
  disponibilitaDelGiorno.value = [];
  aggiornaDisponibilita();
}

async function generaDisponibilita() {
  if (!nuovaOraInizio.value || !nuovaOraFine.value) {
    toast.add({ severity: 'error', summary: 'Errore', detail: 'Seleziona orario inizio e fine', life: 3000 });
    return;
  }
  if (nuovaOraInizio.value >= nuovaOraFine.value) {
    toast.add({ severity: 'error', summary: 'Errore', detail: "L'ora di inizio deve essere precedente alla fine", life: 3000 });
    return;
  }

  const startDate = new Date(giornoSelezionato.value);
  startDate.setHours(0, 0, 0, 0);
  
  let endDate = new Date(startDate);
  if (dataFineGenerazione.value) {
    endDate = new Date(dataFineGenerazione.value);
    endDate.setHours(0, 0, 0, 0);
  }
  
  if (endDate < startDate) {
    endDate = new Date(startDate);
  }

  // Genera slot da 30 min
  const slots = [];
  
  let currentGiorno = new Date(startDate);
  while (currentGiorno <= endDate) {
    const giornoStr = formatDateLocal(currentGiorno);
    
    const inizio = new Date(currentGiorno);
    inizio.setHours(nuovaOraInizio.value.getHours(), nuovaOraInizio.value.getMinutes(), 0, 0);
    const fine = new Date(currentGiorno);
    fine.setHours(nuovaOraFine.value.getHours(), nuovaOraFine.value.getMinutes(), 0, 0);

    let corrente = new Date(inizio);
    while (corrente < fine) {
      const oraInizio = corrente.toTimeString().slice(0, 5);
      const prossimoSlot = new Date(corrente);
      prossimoSlot.setMinutes(prossimoSlot.getMinutes() + 30);
      const oraFine = prossimoSlot.toTimeString().slice(0, 5);
      slots.push({ giorno: giornoStr, ora_inizio: oraInizio, ora_fine: oraFine });
      corrente = prossimoSlot;
    }
    
    // Avanza di una settimana
    currentGiorno.setDate(currentGiorno.getDate() + 7);
  }

  loading.value = true;
  try {
    const promises = slots.map(s => service.addAppuntamentoDisponibilita({
      id_personale: responsabileSelezionato.value.id,
      giorno: s.giorno,
      ora_inizio: s.ora_inizio,
      ora_fine: s.ora_fine,
      max_prenotazioni: nuovoMaxPrenotazioni.value,
      note: nuoveNote.value || ''
    }));
    await Promise.allSettled(promises);
    toast.add({ severity: 'success', summary: 'Successo', detail: `${slots.length} slot generati!`, life: 3000 });
    nuovaOraInizio.value = null;
    nuovaOraFine.value = null;
    nuoveNote.value = '';
    await aggiornaDisponibilita();
    await caricaProspetto();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Errore', detail: 'Impossibile generare le disponibilità', life: 3000 });
  } finally {
    loading.value = false;
  }
}

async function eliminaDisponibilita(dispo) {
  loading.value = true;
  try {
    await service.deleteAppuntamentoDisponibilita({ id: dispo.id });
    toast.add({ severity: 'info', summary: 'Eliminato', detail: 'Disponibilità rimossa', life: 3000 });
    await aggiornaDisponibilita();
    await caricaProspetto();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Errore', detail: 'Impossibile eliminare', life: 3000 });
  } finally {
    loading.value = false;
  }
}

function apriDialogPrenota(dispo) {
  disponibilitaSelezionata.value = dispo;
  atletaSelezionato.value = null;
  notaPrenotazione.value = '';
  dialogPrenota.value = true;
}

async function confermaPrenotazione() {
  if (!disponibilitaSelezionata.value) return;
  loading.value = true;
  try {
    await service.prenotaAppuntamentoAdmin({
      id_disponibilita: disponibilitaSelezionata.value.id,
      id_atleta: atletaSelezionato.value?.id ?? null,
      nota: notaPrenotazione.value || ''
    });
    toast.add({ severity: 'success', summary: 'Prenotato', detail: 'Appuntamento prenotato con successo!', life: 3000 });
    dialogPrenota.value = false;
    await aggiornaDisponibilita();
    await caricaProspetto();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Errore', detail: 'Impossibile prenotare', life: 3000 });
  } finally {
    loading.value = false;
  }
}

async function cancellaPrenotazione(id_prenotazione) {
  loading.value = true;
  try {
    await service.cancellaPrenotazioneAppuntamentoAdmin({ id_prenotazione });
    toast.add({ severity: 'info', summary: 'Cancellato', detail: 'Prenotazione cancellata', life: 3000 });
    await aggiornaDisponibilita();
    await caricaProspetto();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Errore', detail: 'Impossibile cancellare la prenotazione', life: 3000 });
  } finally {
    loading.value = false;
  }
}

async function caricaProspetto() {
  prospettoLoading.value = true;
  try {
    const res = await service.getStatisticheProspetto();
    prospettoGiorni.value = res.items?.giorni || [];
    prospettoResponsabili.value = res.items?.responsabili || [];
    prospettoMatrice.value = res.items?.matrice || {};
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Errore', detail: 'Impossibile caricare il prospetto', life: 3000 });
  } finally {
    prospettoLoading.value = false;
  }
}

function apriDettaglio(responsabile, giorno) {
  activeTab.value = 0;
  responsabileSelezionato.value = responsabili.value.find(r => r.id === responsabile.id);
  giornoSelezionato.value = new Date(giorno);
  onResponsabileChange();
}

onMounted(() => {
  caricaResponsabili();
  caricaAtleti();
  aggiornaDisponibilita();
  caricaProspetto();
});
</script>

<style scoped>
.p-card {
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius);
}
</style>
